















































import { defineComponent, useRouter, ref } from '@nuxtjs/composition-api'
import { storeToRefs } from 'pinia'
import isEmpty from 'lodash/fp/isEmpty'

// Composables
import useKnowledgeBase from '@/composables/learn-and-support/useKnowledgeBase'
import useSearchAnalytics from '@/composables/analytics/useSearchAnalytics'

// Store
import { useKnowledgeBaseStore } from '~/store/knowledge-base'

// Common
import { decodeHTMLEntities } from '@/common/utils/characters'

// Components
import IconFa6SolidXmark from '~icons/fa6-solid/xmark'
import { QueryStyle } from '~/types/search/searchTypes'

export default defineComponent({
  name: 'SupportSearchInputField',
  components: {
    IconFa6SolidXmark,
  },
  props: {
    inputStyle: {
      type: String,
      default: 'light',
      required: false,
    },
    placeholder: {
      type: String,
      default: 'Search Support Articles',
      required: false,
    },
  },
  setup() {
    const knowledgeBaseStore = useKnowledgeBaseStore()
    const { searchInput, typeahead, knowledgebaseErrors } = storeToRefs(knowledgeBaseStore)
    const { hasTypeaheadResults, getSearchResults, getTypeaheadResults, getSupportArticlesLink } = useKnowledgeBase()
    const { trackSearchResults } = useSearchAnalytics()
    const router = useRouter()
    const typeaheadSearchInput = ref('')

    /**
     * set search input in KB store
     */
    const onInput = (e: Event) => {
      // Set search input in store
      const target = e.target as HTMLInputElement
      knowledgeBaseStore.setSearchInput(encodeURIComponent(target.value))
      knowledgeBaseStore.setTypedSearchInput(target.value)

      // Set selectedIndex to 0
      typeahead.value.selectedIndex = 0
      typeaheadSearchInput.value = ''

      // If search input is empty, clear typeahead results
      if (target.value.length === 0) {
        knowledgeBaseStore.clearResults('typeahead')
        return
      }

      // Else get typeahead results
      getTypeaheadResults()
    }

    /**
     * Search & redirect to articles page
     */
    const search = async () => {
      knowledgeBaseStore.setCurrentPage(1)

      // Search
      await getSearchResults()

      // Clear typeahead results
      knowledgeBaseStore.clearResults('typeahead')

      // Send analytics on every knowledge base search
      if (knowledgeBaseStore.searchInput) {
        knowledgeBaseStore.setQueryStyle(
          knowledgeBaseStore.typedSearchInput === knowledgeBaseStore.searchInput
            ? QueryStyle.KEYWORD
            : QueryStyle.TYPEAHEAD
        )

        trackSearchResults({
          searchTerm: knowledgeBaseStore.searchInput,
          searchType: 'knowledgebase',
          searchResults: knowledgeBaseStore.getSearchCount,
        })
      }

      // Redirect to articles page
      router.push(getSupportArticlesLink())
    }

    /**
     * On enter, perform search and clear input field
     */
    const onEnter = (e: Event) => {
      // Reset input
      const target = e.target as HTMLInputElement
      target.blur()

      // Search
      search()
    }

    /**
     * On clear typeahead results, clear search input and typeahead results
     */
    const clearTypeaheadResults = () => {
      knowledgeBaseStore.setSearchInput('')
      knowledgeBaseStore.clearResults('typeahead')
    }

    /**
     * Take the new selectedIndex and set the new search input based on this
     */
    const setCurrentTypeaheadSearchInput = () => {
      const index = typeahead.value.selectedIndex - 1
      if (
        typeahead.value.results?.results &&
        typeahead.value.results?.results[index] &&
        typeahead.value.results?.results[index].title
      ) {
        knowledgeBaseStore.setSearchInput(encodeURIComponent(typeahead.value.results?.results[index].title!.raw))
      }
    }

    /**
     * on arrow down, change selectedIndex to highlight and select typeahead results
     */
    const onArrowDown = () => {
      // Skip if we don't have results
      if (typeahead.value.results.results === undefined || typeahead.value.results.results.length === 0) return

      // Skip if we reached the end of the list
      if (typeahead.value.selectedIndex === typeahead.value.results.results.length) return

      // Increment selectedIndex
      typeahead.value.selectedIndex++

      // Set stored search input
      if (isEmpty(typeahead.value.storedSearchInput)) {
        typeahead.value.storedSearchInput = searchInput.value
      }

      // Change searchinput to current value
      setCurrentTypeaheadSearchInput()
    }

    /**
     * on arrow down, change selectedIndex to highlight and select typeahead results
     */
    const onArrowUp = () => {
      // If so, do nothing
      if (typeahead.value.selectedIndex === 0) return

      // If it 1 and we go to 0, clear stored search input
      if (typeahead.value.selectedIndex === 1) {
        knowledgeBaseStore.setSearchInput(encodeURIComponent(typeahead.value.storedSearchInput))
        typeahead.value.storedSearchInput = ''
      }

      // Decrement selectedIndex
      typeahead.value.selectedIndex--

      // Set the new typeahead suggestion as search input
      setCurrentTypeaheadSearchInput()
    }

    return {
      clearTypeaheadResults,
      onArrowDown,
      onArrowUp,
      searchInput,
      decodeHTMLEntities,
      onInput,
      onEnter,
      search,
      hasTypeaheadResults,
      knowledgebaseErrors,
    }
  },
})
